import React, { useState, useRef, useEffect, useCallback, isValidElement, useMemo } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import { FullLayoutProvider } from './context';
import { HEADER_NAV_HEIGHT } from '../header-nav';
import * as Compounds from './components';
import BlurryBlob, { GooFilter } from './blurry-blob';

import { ScrollArea } from '@/components/ui/scroll-area';
import { FeatureFlagProvider } from '@/lib/feature-flags';
import type { FeatureFlags } from '@/lib/feature-flags';
import { cn } from '@/lib/utils';

const MIN_SIDENAV_WIDTH = 260;
const CLOSE_SIDENAV_THRESHOLD = MIN_SIDENAV_WIDTH - 32;
const MAX_SIDENAV_WIDTH = 600;
const RIGHT_SIDEBAR_WIDTH = 384;

export interface FullLayoutRootProps {
  children?: React.ReactNode;
  disableLeftSideNav?: boolean;
  /** When true, main column uses a plain div (no Radix ScrollArea); viewport height is applied on `FullLayout.Main`. */
  disableMainScrollArea?: boolean;
  className?: string;
  /** When true, the right sidebar is always open and cannot be toggled. */
  forceRightSidebarOpen?: boolean;
}

// Helper function to extract slots from children
function extractSlots<T extends Record<string, React.ComponentType<any>>>(
  children: React.ReactNode,
  refs: T,
): { [K in keyof T]: React.ReactElement | null } {
  const out = {} as { [K in keyof T]: React.ReactElement | null };
  const arr = React.Children.toArray(children);
  for (const key of Object.keys(refs) as (keyof T)[]) {
    const match = arr.find((c): c is React.ReactElement => isValidElement(c) && c.type === refs[key]);
    out[key] = match ?? null;
  }

  return out;
}

// --- Root component ---

function FullLayoutRoot({
  children,
  disableLeftSideNav = false,
  disableMainScrollArea = false,
  className,
  forceRightSidebarOpen = false,
}: FullLayoutRootProps) {
  // Match isMobile breakpoint (max-width: 767px) so no flash on 768–1023px
  const [isSideNavOpen, setIsSideNavOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth > 767);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(forceRightSidebarOpen);
  const [isResizing, setIsResizing] = useState(false);
  const [gridWidth, setGridWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 0));

  const newMinNavWidth = useMemo(
    () =>
      gridWidth > 0
        ? Math.max(MIN_SIDENAV_WIDTH, Math.min(MAX_SIDENAV_WIDTH, Math.round(gridWidth * 0.18)))
        : MIN_SIDENAV_WIDTH,
    [gridWidth],
  );
  const [leftNavWidth, setLeftNavWidth] = useState(newMinNavWidth);

  const sideNavRef = useRef<HTMLDivElement>(null);
  const rightSidebarRef = useRef<HTMLElement>(null);
  const sidebarToggleRef = useRef<HTMLButtonElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const prevSideNavOpenRef = useRef(isSideNavOpen);
  const resizeAcRef = useRef<AbortController | null>(null);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isWideDesktop = useMediaQuery('(min-width: 1280px)');

  // Prevent native scrollbar from appearing
  useEffect(() => {
    if (!document.body.querySelector('.sbdocs')) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Handle grid width changes
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      setGridWidth(entry.contentRect.width);
    });
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  // Close other sidebar if opening a new one on wide desktop
  const closeOtherSidebar = useCallback(
    (opening: 'left' | 'right') => {
      if (isWideDesktop) return;
      if (opening === 'left') setIsRightSidebarOpen(false);
      else setIsSideNavOpen(false);
    },
    [isWideDesktop],
  );

  // Show left sidebar
  const showSidenav = useCallback(() => {
    closeOtherSidebar('left');
    setIsSideNavOpen(true);
    setLeftNavWidth(newMinNavWidth);
    sideNavRef.current?.focus();
  }, [closeOtherSidebar, newMinNavWidth]);

  // Toggle left sidebar
  const toggleSidebar = useCallback(() => {
    closeOtherSidebar('left');
    setIsSideNavOpen((prev) => !prev);
  }, [closeOtherSidebar]);

  // Open right sidebar (no-op if already open)
  const openRightSidebar = useCallback(() => {
    closeOtherSidebar('right');
    setIsRightSidebarOpen(true);
  }, [closeOtherSidebar]);

  // Close right sidebar (no-op if already closed)
  const closeRightSidebar = useCallback(() => {
    setIsRightSidebarOpen(false);
  }, []);

  // Toggle right sidebar
  const toggleRightSidebar = useCallback(() => {
    if (forceRightSidebarOpen) return;
    closeOtherSidebar('right');
    setIsRightSidebarOpen((prev) => !prev);
  }, [closeOtherSidebar, forceRightSidebarOpen]);

  // Apply left nav width - used for resizing left sidebar
  const applyLeftNavWidth = useCallback(
    (newWidth: number): boolean => {
      if (newWidth < CLOSE_SIDENAV_THRESHOLD) {
        setLeftNavWidth(newMinNavWidth);
        setIsSideNavOpen(false);

        return true;
      } else {
        // clamp to MAX_SIDENAV_WIDTH
        setLeftNavWidth(Math.min(MAX_SIDENAV_WIDTH, newWidth));

        return false;
      }
    },
    [newMinNavWidth],
  );

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizeAcRef.current = new AbortController();
    setIsResizing(true);
  }, []);

  // Handle resize end
  const endResize = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Handle mouse move during resize
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;
      if (applyLeftNavWidth(e.clientX)) {
        endResize();
      }
    },
    [isResizing, applyLeftNavWidth, endResize],
  );

  // Handle mouse up during resize
  const handleMouseUp = useCallback(() => endResize(), [endResize]);

  // Set inert attribute on side nav and right sidebar - disables tabbing to elements inside when sidebar is closed
  const setInert = useCallback((el: HTMLElement | null, open: boolean) => {
    if (!el) return;
    if (open) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  }, []);

  useEffect(() => {
    setInert(sideNavRef.current, isSideNavOpen);
    setInert(rightSidebarRef.current, isRightSidebarOpen);
  }, [isSideNavOpen, isRightSidebarOpen]);

  // Attach resize listeners when isResizing becomes true (controller already set in handleResizeStart)
  useEffect(() => {
    if (!isResizing || !resizeAcRef.current) return;

    const { signal } = resizeAcRef.current;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove, { signal });
    document.addEventListener('mouseup', handleMouseUp, { signal });
    window.addEventListener('mouseup', handleMouseUp, { signal });
    setInert(sideNavRef.current, false);

    return () => {
      resizeAcRef.current?.abort();
      resizeAcRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setInert(sideNavRef.current, true);
    };
  }, [isResizing]);

  // Handle resize key down
  const handleResizeKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const step = 16;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        applyLeftNavWidth(leftNavWidth - step);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        applyLeftNavWidth(Math.min(MAX_SIDENAV_WIDTH, leftNavWidth + step));
      }
    },
    [leftNavWidth, applyLeftNavWidth],
  );

  const leftCol = disableLeftSideNav ? 0 : isSideNavOpen ? (isMobile ? gridWidth : leftNavWidth) : 0;
  const rightCol = isMobile ? 0 : isRightSidebarOpen ? RIGHT_SIDEBAR_WIDTH : 0;

  // Set left sidebar open state based on mobile
  useEffect(() => {
    setIsSideNavOpen(isMobile ? false : true);
  }, [isMobile]);

  // Focus sidebar toggle when left sidebar is closed
  useEffect(() => {
    if (prevSideNavOpenRef.current && !isSideNavOpen) {
      sidebarToggleRef.current?.focus();
    }
    prevSideNavOpenRef.current = isSideNavOpen;
  }, [isSideNavOpen]);

  const ariaRange = MAX_SIDENAV_WIDTH - newMinNavWidth;
  const ariaValuenow = ariaRange === 0 ? 100 : Math.round(((leftNavWidth - newMinNavWidth) / ariaRange) * 100);

  const layoutContextValue = useMemo(
    () => ({
      isSideNavOpen,
      isRightSidebarOpen,
      toggleSidebar,
      openRightSidebar,
      closeRightSidebar,
      toggleRightSidebar,
      showSidenav,
      disableLeftSideNav: disableLeftSideNav ?? false,
      leftNavWidth,
      isMobile,
      sideNavRef,
      rightSidebarRef,
      sidebarToggleRef,
      handleResizeStart,
      handleResizeKeyDown,
      ariaValuenow,
      ariaValuemin: 0,
      ariaValuemax: 100,
      disableMainScrollArea,
    }),
    [
      isSideNavOpen,
      isRightSidebarOpen,
      toggleSidebar,
      openRightSidebar,
      closeRightSidebar,
      toggleRightSidebar,
      showSidenav,
      leftNavWidth,
      isMobile,
      disableLeftSideNav,
      disableMainScrollArea,
      handleResizeStart,
      handleResizeKeyDown,
      ariaValuenow,
    ],
  );

  // --- Extract slots from children ---
  const { MainArea: mainArea, RightSidebar: rightSidebar } = extractSlots(children, {
    MainArea: Compounds.MainArea,
    RightSidebar: Compounds.RightSidebar,
  });
  const mainAreaChildren = isValidElement(mainArea)
    ? (mainArea.props as { children?: React.ReactNode }).children
    : null;
  const { Header: header, Body: body } = extractSlots(mainAreaChildren ?? null, {
    Header: Compounds.Header,
    Body: Compounds.Body,
  });
  const bodyChildren = isValidElement(body) ? (body.props as { children?: React.ReactNode }).children : null;
  const { LeftSidebar: leftSidebar, Main: main } = extractSlots(bodyChildren ?? null, {
    LeftSidebar: Compounds.LeftSidebar,
    Main: Compounds.Main,
  });

  const hasRightSidebar = rightSidebar != null;
  const mainPanelClasses =
    'md:shadow-[2px_4px_12px_0_rgba(0,0,0,0.14)] md:dark:shadow-[2px_4px_12px_0_rgba(255,255,255,0.14)] md:rounded-lg';

  // --- Render the layout ---
  return (
    <FullLayoutProvider value={layoutContextValue}>
      <GooFilter />
      <BlurryBlob variant="topLeft" />
      <BlurryBlob variant="bottomRight" />
      <div
        className={cn(
          'min-h-screen w-screen bg-background grid transition-[grid-template-columns] duration-300 ease-in-out',
          className,
        )}
        style={{
          gridTemplateColumns: hasRightSidebar ? 'minmax(0, 1fr) var(--right-col)' : 'minmax(0, 1fr)',
          ['--right-col' as string]: hasRightSidebar ? `${rightCol}px` : '0px',
          ['--header-nav-height' as string]: `${HEADER_NAV_HEIGHT}px`,
        }}
      >
        <div className="min-w-0 grid grid-rows-[auto_1fr]">
          {header}

          <div
            ref={gridRef}
            data-sidenav-state={isSideNavOpen ? 'open' : 'closed'}
            data-right-sidebar-state={isRightSidebarOpen ? 'open' : 'closed'}
            className={cn(
              'group/sidenav group/rightsidebar min-h-0 min-w-0 grid transition-[grid-template-columns] ease-linear',
            )}
            style={{
              gridTemplateColumns: `var(--left-col) minmax(0, 1fr)`,
              ['--left-col' as string]: `${leftCol}px`,
            }}
          >
            {disableLeftSideNav ? <div className="w-0" /> : leftSidebar}
            <div className={cn('relative md:mr-4', { 'md:mx-4': !isSideNavOpen || disableLeftSideNav })}>
              {disableMainScrollArea ? (
                <div className={cn('bg-surface', mainPanelClasses)}>
                  <main className="min-w-0 relative">{main}</main>
                </div>
              ) : (
                <>
                  <ScrollArea
                    className={cn(
                      'bg-surface h-[calc(100dvh-var(--header-nav-height))] md:h-[calc(100dvh-var(--header-nav-height)-1rem)]',
                      mainPanelClasses,
                    )}
                  >
                    <div className="min-h-full">
                      <main className={cn('min-w-0 overflow-visible relative p-4 md:p-8')}>{main}</main>
                    </div>
                  </ScrollArea>
                  <div
                    aria-hidden
                    className="main-scroll-fade pointer-events-none sticky bottom-4 left-0 right-0 h-16 bg-gradient-to-t from-surface to-transparent md:rounded-b-lg"
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {hasRightSidebar ? rightSidebar : null}
      </div>
    </FullLayoutProvider>
  );
}

/**
 * Custom memo comparator: re-render only when these props change by reference/value.
 * Use this so changing other props (e.g. from a parent) does not force the layout shell to re-render.
 */
function fullLayoutRootPropsAreEqual(prev: FullLayoutRootProps, next: FullLayoutRootProps): boolean {
  return (
    prev.children === next.children &&
    prev.className === next.className &&
    prev.disableLeftSideNav === next.disableLeftSideNav &&
    prev.disableMainScrollArea === next.disableMainScrollArea
  );
}

const FullLayoutRootMemo = React.memo(FullLayoutRoot, fullLayoutRootPropsAreEqual);

export interface FullLayoutProps extends FullLayoutRootProps {
  /** Feature flags for layout/header (e.g. platform.brainfish-native.enabled). Passed through to FeatureFlagProvider. */
  featureFlags?: FeatureFlags;
}

function FullLayoutWithFlags(props: FullLayoutProps) {
  const { featureFlags, ...layoutProps } = props;

  return (
    <FeatureFlagProvider flags={featureFlags}>
      <FullLayoutRootMemo {...layoutProps} />
    </FeatureFlagProvider>
  );
}

type FullLayoutCompound = typeof FullLayoutWithFlags & {
  MainArea: typeof Compounds.MainArea;
  Body: typeof Compounds.Body;
  Header: typeof Compounds.Header;
  LeftSidebar: typeof Compounds.LeftSidebar;
  Main: typeof Compounds.Main;
  RightSidebar: typeof Compounds.RightSidebar;
};

const FullLayout: FullLayoutCompound = Object.assign(FullLayoutWithFlags, {
  MainArea: Compounds.MainArea,
  Body: Compounds.Body,
  Header: Compounds.Header,
  LeftSidebar: Compounds.LeftSidebar,
  Main: Compounds.Main,
  RightSidebar: Compounds.RightSidebar,
});

export default FullLayout;
